import { CommonModule } from '@angular/common';
import { Component, computed, inject, signal } from '@angular/core';
import { FormControl, ReactiveFormsModule } from '@angular/forms';
import { MatDialogModule, MatDialogRef } from '@angular/material/dialog';
import { LetDirective } from '@ngrx/component';
import { Store } from '@ngrx/store';

import { MeetingBotStatus } from '@ninety/platform/models/meetings/meeting-bot';
import { MeetingRoutes } from '@ninety/platform/models/meetings/meeting-routes';
import { MeetingType } from '@ninety/platform/models/meetings/meeting-type.enum';
import { TeamSelectors, selectCurrentUserIsManagerOrAbove, selectLanguage } from '@ninety/platform/state';
import {
  TerraDividerModule,
  TerraErrorModule,
  TerraFormFieldModule,
  TerraIconModule,
  TerraTextInputModule,
} from '@ninety/terra';
import { DateDistancePipe, ButtonComponent } from '@ninety/ui/core';
import {
  CalendarEvent,
  CalendarEventsService,
  ChooseCalendarProviderDialogComponent,
  GoogleCalendarService,
  isOutlookCalendarProvider,
  NativeIntegration,
  NativeIntegrationCoreService,
  OutlookCalendarService,
} from '@ninety/web/pages/shared/integrations';

import { MeetingBotCalendarSectionComponent } from '../../_shared/meeting-bot-calendar-section/meeting-bot-calendar-section.component';
import { validateMeetingLink } from '../../_shared/util/meeting-link.validator';
import { MeetingDialogActions, MeetingsTabsActions } from '../../_state/meetings.actions';
import { MeetingsStateSelectors } from '../../_state/meetings.selectors';

import { SelectCalendarEventDialogComponent } from './select-calendar-event-dialog/select-calendar-event-dialog.component';

@Component({
  selector: 'ninety-start-meeting-dialog',
  standalone: true,
  imports: [
    CommonModule,
    ReactiveFormsModule,
    TerraDividerModule,
    TerraFormFieldModule,
    TerraIconModule,
    TerraTextInputModule,
    TerraErrorModule,
    LetDirective,
    ButtonComponent,
    DateDistancePipe,
    MatDialogModule,
    SelectCalendarEventDialogComponent,
    MeetingBotCalendarSectionComponent,
    ChooseCalendarProviderDialogComponent,
  ],
  templateUrl: './start-meeting-dialog.component.html',
  styles: [
    `
      @use 'terra';
      @use 'media-queries' as *;

      :host {
        display: flex;
        flex-direction: column;
        gap: 4px;
        width: 350px;
        @include gt-xs {
          width: 457px;
        }
      }
      .header-container {
        padding: 0 4px 16px 4px;
        ninety-button {
          margin-top: -1px;
        }
      }
      h1.terra-title {
        margin-bottom: 6.5px;
      }

      .menu-item {
        display: flex;
        flex-direction: row;
        justify-content: flex-start;
        align-items: center;
        gap: 8px;
        cursor: pointer;
        height: 36px;

        .flex-2 {
          flex: 0 1 auto;
          min-width: 0;
          overflow: hidden;
          text-overflow: ellipsis;
          white-space: nowrap;
        }

        .text-light {
          flex-shrink: 1;
        }

        .start-meeting-label {
          margin-left: auto;
          flex-shrink: 0;
          color: terra.color('brand');
        }
      }
      .header {
        padding-left: 4px;
      }
      .recent-meetings-divider {
        margin-bottom: 16px;
      }
      .meeting-link-field {
        width: 100%;
        margin-bottom: 12px;
      }
      .meeting-link-valid-icon {
        color: terra.color('green');
      }
      .text-light,
      .header {
        color: terra.color('text', 'light');
      }
      terra-icon {
        color: terra.color('text', 'lightest');
      }

      mat-dialog-content {
        padding: 20px !important;
        flex: 1;

        &.dialog-container--unpadded {
          padding: 0 !important;
        }
      }

      mat-dialog-actions {
        box-shadow: 0px -1px 2px 1px terra.color('shadow', 'light');
        padding: 2px 16px !important;
        margin-top: -2px !important;
        justify-content: flex-start !important;
      }
    `,
  ],
})
export class StartMeetingDialogComponent {
  private readonly store = inject(Store);
  private readonly dialogRef = inject(MatDialogRef<StartMeetingDialogComponent>);
  private readonly nativeIntegrationService = inject(NativeIntegrationCoreService);
  private readonly googleCalendarService = inject(GoogleCalendarService);
  private readonly outlookCalendarService = inject(OutlookCalendarService);

  readonly recentMeetings$ = this.store.select(MeetingsStateSelectors.selectRecentMeetingsForStartDialog);
  readonly defaultMeetings$ = this.store.select(MeetingsStateSelectors.selectDefaultMeetingOptions);

  customMeetings$ = this.store.select(MeetingsStateSelectors.selectCustomMeetingSettings);
  language$ = this.store.select(selectLanguage);
  currentTeam$ = this.store.select(TeamSelectors.selectFilterBarTeam);
  /** Agenda editing is Manager+ (the Agendas tab and its routes are gated the same way). */
  readonly canManageAgendas$ = this.store.select(selectCurrentUserIsManagerOrAbove);
  meetingType = MeetingType;

  readonly addMeetingAgentControl = new FormControl(false);
  readonly meetingLinkControl = new FormControl('', validateMeetingLink);

  // Only needed here to resolve the connectionId for the swapped event picker below - the rest of
  // the calendar-connect UI/state lives in MeetingBotCalendarSectionComponent.
  readonly activeCalendarConnectionId = computed(() => this.nativeIntegrationService.activeCalendarConnectionId());

  readonly calendarEventsService = computed<CalendarEventsService>(() => {
    const integration = this.nativeIntegrationService.activeCalendarIntegration();
    return integration && isOutlookCalendarProvider(integration)
      ? this.outlookCalendarService
      : this.googleCalendarService;
  });

  readonly selectedCalendarEvent = signal<CalendarEvent | undefined>(undefined);

  // Swaps this dialog's own content for the event picker in place, rather than opening it as a
  // nested modal on top.
  readonly showEventPicker = signal(false);

  // Same swap-in-place pattern for the choose-provider step, so the meeting dialog stays in the
  // background instead of a second dialog stacking on top of it.
  readonly showProviderChooser = signal(false);
  readonly providerChooserIntegrations = signal<NativeIntegration[]>([]);

  constructor() {
    this.nativeIntegrationService.fetchNativeIntegrations();
  }

  openEventPicker(): void {
    if (!this.activeCalendarConnectionId()) {
      return;
    }
    this.showEventPicker.set(true);
  }

  onEventPickerClosed(event: CalendarEvent | undefined): void {
    this.showEventPicker.set(false);
    if (!event) {
      return;
    }
    this.selectedCalendarEvent.set(event);
    this.meetingLinkControl.setValue(event.meetingUrl ?? '');
  }

  onOpenProviderChooser(integrations: NativeIntegration[]): void {
    this.providerChooserIntegrations.set(integrations);
    this.showProviderChooser.set(true);
  }

  onProviderChooserClosed(): void {
    this.showProviderChooser.set(false);
  }

  createMeeting(meetingType: MeetingType, agendaId?: string) {
    const addMeetingAgent = this.addMeetingAgentControl.value;
    if (addMeetingAgent && this.meetingLinkControl.invalid) {
      this.meetingLinkControl.markAsTouched();
      return;
    }

    this.dialogRef.close();
    const link = addMeetingAgent ? this.meetingLinkControl.value?.trim() : undefined;
    this.store.dispatch(
      MeetingDialogActions.createMeeting({
        meetingType,
        agendaId,
        meetingBot: link ? { extMeetingUrl: link, status: MeetingBotStatus.creating } : undefined,
      })
    );
  }

  manageAgendas() {
    this.dialogRef.close();

    this.store.dispatch(MeetingsTabsActions.navigate({ route: MeetingRoutes.agendas }));
  }
}
